import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageHero, SectionHeader } from '@/components/common/Sections';
import { CTABanner } from '@/components/common/Cards';
import { IMAGES } from '@/lib/constants';
import { useCMS } from '@/lib/useCMS';
import { CalendarBlank, ArrowRight } from '@phosphor-icons/react';

const defaultArticles = [
  { id: 1, title: "Osaka Group Launches Revolutionary Travel Super App", content: "Osaka Connect brings 24 integrated travel products to your fingertips.", date: "March 15, 2024", image_url: IMAGES.corporateTravel },
  { id: 2, title: "New Office Opening in London", content: "Osaka Group strengthens its global footprint with a new office in London.", date: "February 28, 2024", image_url: IMAGES.airport },
  { id: 3, title: "UAE Visa Processing Made Faster", content: "Get your Dubai visa in as little as 24 hours with our new express service.", date: "February 10, 2024", image_url: IMAGES.dubai },
  { id: 4, title: "Osaka Educare Partners with Top UK Universities", content: "New partnerships offer exclusive scholarship opportunities for Indian students.", date: "January 25, 2024", image_url: IMAGES.studyAbroad },
];

export default function NewsPage() {
  const { news } = useCMS();
  const newsArticles = news.length > 0 ? news : defaultArticles;
  const featuredArticle = newsArticles[0];
  const otherArticles = newsArticles.slice(1);

  return (
    <div className="page-transition">
      <PageHero title="News & Media" subtitle="Stay updated with the latest from Osaka Group" backgroundImage={IMAGES.airport} />

      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} data-testid="featured-article" className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="img-zoom"><img src={featuredArticle.image_url || featuredArticle.image} alt={featuredArticle.title} className="w-full aspect-[16/10] object-cover" /></div>
            <div>
              <span className="inline-block px-3 py-1 bg-[#D4AF37] text-[#0A1F3D] text-xs font-bold mb-4">FEATURED</span>
              <h2 className="text-3xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-4">{featuredArticle.title}</h2>
              <p className="text-[#4A5568] leading-relaxed mb-4">{featuredArticle.content}</p>
              <div className="flex items-center gap-4 text-sm text-[#4A5568] mb-6"><CalendarBlank size={16} />{featuredArticle.date}</div>
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="news-grid" className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <SectionHeader overline="Latest Updates" title="All News & Articles" />
          <div className="grid md:grid-cols-3 gap-8">
            {otherArticles.map((article, index) => (
              <motion.article key={article.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} data-testid={`news-card-${article.id}`} className="bg-white border border-slate-200 overflow-hidden card-hover group">
                <div className="img-zoom"><img src={article.image_url || article.image} alt={article.title} className="w-full aspect-[16/10] object-cover" /></div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-[#4A5568] mb-3"><CalendarBlank size={14} />{article.date}</div>
                  <h3 className="text-lg font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-3 group-hover:text-[#D4AF37]">{article.title}</h3>
                  <p className="text-[#4A5568] text-sm line-clamp-2 mb-4">{article.content}</p>
                  <Link to="#" className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-medium">Read More <ArrowRight size={16} /></Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
